import { useState } from 'react';
import { useT } from '@kmgestao/i18n';
import { Badge, Button, PageSectionCard, Skeleton } from '@kmgestao/ui';

import { useClinicByok } from '../../../../features/ai-clinic/index.js';

import { ByokDeleteDialog } from './ByokDeleteDialog.js';
import { ByokForm } from './ByokForm.js';

export interface ByokCardProps {
  clinicId: string;
}

export function ByokCard({ clinicId }: ByokCardProps) {
  const { t } = useT('ai-clinic');
  const { data: byok, isLoading, isError, error } = useClinicByok(clinicId);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <PageSectionCard
      title={t('byok.title')}
      description={t('byok.description')}
      contentClassName="space-y-4"
    >
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-11 w-40" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">{error instanceof Error ? error.message : '—'}</p>
      ) : byok ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">{t('byok.configured.title')}</Badge>
            {byok.verificationStatus === 'invalid' ? (
              <Badge variant="destructive">⚠ invalid</Badge>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-base sm:grid-cols-[auto_1fr]">
            <span className="text-muted-foreground">{t('byok.configured.providerLabel')}:</span>
            <span>{byok.providerName}</span>
            <span className="text-muted-foreground">{t('byok.configured.last4Label')}:</span>
            <span className="font-mono">…{byok.apiKeyLast4}</span>
            {byok.modelId ? (
              <>
                <span className="text-muted-foreground">{t('byok.configured.modelLabel')}:</span>
                <span className="font-mono">{byok.modelId}</span>
              </>
            ) : null}
            {byok.nickname ? (
              <>
                <span className="text-muted-foreground">{t('byok.configured.nicknameLabel')}:</span>
                <span>{byok.nickname}</span>
              </>
            ) : null}
            <span className="text-muted-foreground">{t('byok.configured.updatedLabel')}:</span>
            <span className="text-sm">{new Date(byok.updatedAt).toLocaleString('pt-BR')}</span>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" onClick={() => setFormOpen(true)} className="w-full sm:w-auto">
              {t('byok.actions.rotate')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              className="w-full sm:w-auto"
            >
              {t('byok.actions.delete')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-dashed border-border bg-muted/20 p-5">
            <p className="text-base font-medium">{t('byok.notConfigured.title')}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {t('byok.notConfigured.description')}
            </p>
          </div>
          <Button type="button" onClick={() => setFormOpen(true)} className="w-full sm:w-auto">
            {t('byok.actions.setup')}
          </Button>
        </div>
      )}

      <ByokForm
        open={formOpen}
        onOpenChange={setFormOpen}
        clinicId={clinicId}
        current={byok ?? null}
      />
      <ByokDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} clinicId={clinicId} />
    </PageSectionCard>
  );
}
